// Angular
import { Params } from '@angular/router';
import { Injectable } from '@angular/core';

// NGRX
import { take } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';

// OLCI
import moment from 'moment';
import { get } from 'lodash';
import * as utils from '../../../util';
import * as fromRoot from '../../../reducers';
import { VdsInfo } from '../../../models/vds-info';
import { constants } from '../../../constants/constants';
import { ReservationRetrievalObject } from '../../../models';
import { olciDataDefault } from '../../testing/mocks/reservationdata.mock';
import { getDateAsObj } from '../../../directives/dob-validator.directive';
import {ObjectManagerUtils} from '../../../utils/object-manager.utils';

@Injectable()
export class GatingServices {
	constructor(
    private readonly store: Store<fromRoot.AppState>,
    private readonly objectManagerUtils: ObjectManagerUtils,
  ) { }

	getReservationData(): ReservationRetrievalObject | any {
		const reservationData = window.sessionStorage.getItem(constants.SESSION_STORAGE_KEYS.OLCI_RESERVATION_DATA);
		return reservationData ? JSON.parse( reservationData ) as ReservationRetrievalObject : null;
	}

	saveLoggedInGuest(loggedInGuestId: string) {
		return window.sessionStorage.setItem(constants.SESSION_STORAGE_KEYS.OLCI_LOGGED_IN_GUEST_ID, JSON.stringify(loggedInGuestId));
	}

	getLoggedInGuest(): string {
		return JSON.parse(window.sessionStorage.getItem(constants.SESSION_STORAGE_KEYS.OLCI_LOGGED_IN_GUEST_ID) || '');
	}

	clearSessionStorage() {
		window.sessionStorage.removeItem(constants.SESSION_STORAGE_KEYS.OLCI_RESERVATION_DATA);
		window.sessionStorage.removeItem(constants.SESSION_STORAGE_KEYS.OLCI_LOGGED_IN_GUEST_ID);
		window.sessionStorage.removeItem(constants.SESSION_STORAGE_KEYS.COOKIE_NULL_VALUES_ERROR_LOGGED);
		window.localStorage.removeItem(constants.LOCAL_STORAGE_KEYS.MOBILE_AD_MODAL);

	}

	setReservationData(data: ReservationRetrievalObject): void {
		const oldData = this.getReservationData();

		if (oldData && oldData.bookingId !== data.bookingId) {
			this.clearSessionStorage();
		}

		window.sessionStorage.setItem(constants.SESSION_STORAGE_KEYS.OLCI_RESERVATION_DATA, JSON.stringify(data));
	}

	getCookie(cookieName: any) {
		return document.cookie
			.split(';')
			.map(function (x: any) {
				return x.trim().split(/(=)/);
			})
			.reduce(function (a: any, b: any) {
				a[b[0]] = a[b[0]] ? a[b[0]] + ', ' + b.slice(2).join('') : b.slice(2).join('');
				return a;
			}, {})[cookieName];
	}

	// Get the accessToken value for gating Endpoint
	accessTokenC() {
		return this.getCookie(constants.COOKIES.OLCI.ACCESS_TOKEN);
	}

	getCheckinDataCookie() {
		try {
			let checkinDataCookie = null;
			const cookieValue = this.getCookie(constants.COOKIES.OLCI.CHECKIN_DATA);
			if (cookieValue) {
				const decoded = window.atob(cookieValue);
				checkinDataCookie = decoded && JSON.parse(decoded);
			}
			return checkinDataCookie;
		} catch (error) {
			return null;
		}
	}

	getOlciDataCookie(): ReservationRetrievalObject {
		const cookieValue: any = utils.getDecodedAccessToken(constants.COOKIES.OLCI.OLCI_DATA);
		let checkinDataCookie = this.getCheckinDataCookie();

		let olciData: ReservationRetrievalObject = olciDataDefault;
		if (cookieValue) {
			if (checkinDataCookie && cookieValue.bookingId !== checkinDataCookie.bookingId) {
				checkinDataCookie = null;
			}
			const sailDate = cookieValue.sailDate ? (cookieValue.sailDate).replace(/-/g, '') : '';
			const sailDateMiddleware = getDateAsObj(cookieValue.sailDate, constants.DATE_FORMAT.SERVER);
			olciData = {
				brand: this.objectManagerUtils.accessObjectByIndex(cookieValue, 'brand', ''),
				lastName: this.objectManagerUtils.accessObjectByIndex(cookieValue, 'lastName', ''),
				bookingId: this.objectManagerUtils.accessObjectByIndex(cookieValue, 'bookingId', get(checkinDataCookie, 'bookingId', '')),
				departureYear: sailDateMiddleware.year,
				departureMonth: sailDateMiddleware.month,
				departureDay: sailDateMiddleware.day,
				sailDate: sailDate,
				shipCode: this.objectManagerUtils.accessObjectByIndex(cookieValue, 'shipCode', ''),
				vdsId: this.objectManagerUtils.accessObjectByIndex(cookieValue, 'vdsId', ''),
				guestId: this.objectManagerUtils.accessObjectByIndex(cookieValue, 'guestId', ''),
				charterSailing: this.objectManagerUtils.accessObjectByIndex(cookieValue, 'charterSailing', false),
				locale: this.objectManagerUtils.accessObjectByIndex(cookieValue, 'locale', 'en'),
				email: this.objectManagerUtils.accessObjectByIndex(cookieValue, 'email', get(checkinDataCookie, 'email', '')),
				dob: this.objectManagerUtils.accessObjectByIndex(cookieValue, 'dob', get(checkinDataCookie, 'dob', '')),
				accountOwnerName: this.objectManagerUtils.accessObjectByIndex(cookieValue, 'accountOwnerName', get(checkinDataCookie, 'accountOwnerName', '')),
			}
		}

		return olciData;
	}

	hasCookieNullValues(cookie: any): boolean {
		return cookie ? Object.values(cookie).some(x => x === null || x === 'null') : false;
	}

	getCookieValueAsString(cookieName: string) {
		return JSON.stringify(utils.getDecodedAccessToken(cookieName));
	}

	validGatingQueryString(queryString: Params): boolean {
		return (
			queryString['brand'] &&
			queryString['bookingId'] &&
			queryString['shipCode'] &&
			queryString['lastName'] &&
			queryString['departureDay'] &&
			queryString['departureMonth'] &&
			queryString['departureYear']
		);
	}

	getBrandURL() {
		return (<any>window).location.origin;
	}

	getGAAccountURL() {
		return constants.ROUTES.GUEST_ACCOUNT;
	}

	getGADashboardURL() {
		return constants.ROUTES.GUEST_ACCOUNT;
	}

	getGACruisesURL() {
		return constants.ROUTES.GUEST_ACCOUNT;
	}

	getGASettingsURL() {
		return '/account/settings';
	}

	getIdToken() {
		const idToken = utils.getDecodedAccessToken(constants.COOKIES.OLCI.ID_TOKEN);

		if (!idToken) {
			return null;
		}

		return idToken;
	}

	get accessToken() {
		const accessToken = utils.readCookie(constants.COOKIES.OLCI.ACCESS_TOKEN);

		if (!accessToken) {
			return null;
		}

		return accessToken;
	}

	hasLoyalty(): boolean {
		const loyaltyData = this.getLoyaltyData();
		return loyaltyData && loyaltyData.loyaltyId;
	}

	getLoyaltyData() {
		let stringChain = '{';
		const loyaltyData = utils.readCookie(constants.COOKIES.OLCI.LOYALTY_DATA);
		if (!loyaltyData) {
			return null;
		}
		loyaltyData.split('|').map(function (x: any) {
			const text = x.trim().split('=');
			if (text[1] !== 'null') {
				text[1] = '"' + text[1] + '"';
			}
			stringChain = stringChain + ('"' + text[0] + '":' + text[1] + ',');
		});
		return JSON.parse(stringChain.substring(0, stringChain.length - 1) + '}');
	}

	getLoggedInGuestInfo(): VdsInfo {
		let result: VdsInfo | any = {};

		this.store
			.pipe(select(fromRoot.getSessionState))
			.pipe(take(1))
			.subscribe(res => {
				result = res.vds;
			});

		return result;
	}

	is18OrMore() {
		let result = false;
		const info = this.getLoggedInGuestInfo();

		this.store
			.pipe(
				select(fromRoot.getSessionVar),
				take(1)
			)
			.subscribe(res => {
				const dob = moment(info.vdsDOB, constants.DATE_FORMAT.GUEST_ACCOUNT).format('L');
				const departureDate = moment(res.departureDate, constants.DATE_FORMAT.SERVER);

				result = departureDate.diff(dob, 'years') >= 18;
			});

		return result;
	}

}
